import React, { useState } from 'react';
import { Settings, Shuffle, MousePointer, Move, Trash2, Target, Sparkles, Database, Plus, HelpCircle, AlertTriangle } from 'lucide-react';
import { motion } from 'framer-motion';
import { useGame } from '../context/GameContext';
import { useAuth } from '../hooks/useAuth';
import { isUserAdmin } from '../utils/admin';
import { seedFirestoreWithPuzzles } from '../services/puzzleService';
import { detectBrowserCapabilities } from '../utils/browserCompat';

interface GameHeaderProps {
  onShowSettings: () => void;
  onShowAdminModal?: () => void;
  onShowHelp: () => void;
}

const GameHeader: React.FC<GameHeaderProps> = ({ onShowSettings, onShowAdminModal, onShowHelp }) => {
  const { gameState, actions } = useGame();
  const { user } = useAuth();
  const [isSeeding, setIsSeeding] = useState(false);
  const isAdmin = isUserAdmin(user);
  const browserCapabilities = detectBrowserCapabilities();


  const handleSeedFirestore = async () => {
    if (isSeeding) return;
    
    setIsSeeding(true);
    try {
      const success = await seedFirestoreWithPuzzles(7);
      if (success) {
        actions.showToast("Successfully seeded 7 days of puzzles!", 'success');
      } else {
        actions.showToast("Failed to seed some puzzles", 'error');
      }
    } catch (error) {
      actions.showToast("Error seeding puzzles", 'error');
    } finally {
      setIsSeeding(false);
    }
  };

  return (
    <motion.div 
      className="bg-white border-b border-gray-200 shadow-sm relative"
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 30, delay: 0.2 }}
    >
      <div className="px-4 py-3 sm:py-4 md:py-5 lg:py-6 h-14 sm:h-16 md:h-18 lg:h-20 flex items-center justify-between">
        {/* Left - App Name */}
        <motion.div 
          className="flex items-center gap-3"
          initial={{ y: 0 }}
          animate={{ y: 0 }}
        >
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
            Pathways
          </h1>
        </motion.div>

        {/* Center - Title and Error Count (horizontal) */}
        <div className="flex items-center gap-4">
          <motion.p 
            className="text-sm sm:text-base font-medium text-gray-600 hidden sm:block"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Daily Word Challenge
          </motion.p>
          <motion.div 
            className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg bg-gray-50 border border-gray-200 flex items-center gap-2"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6 }}
          >
            <Target className="w-4 h-4 text-gray-600" />
            <span className="text-sm sm:text-base font-semibold text-gray-900">
              {gameState.errors}/4
            </span>
          </motion.div>
        </div>

        {/* Right - Control buttons */}
        <div className="flex items-center gap-2">
            {/* Compatibility warning for older iOS */}
            {browserCapabilities.isOldIOS && (
              <motion.div
                className="px-2 py-1 bg-amber-50 rounded-lg text-amber-700 border border-amber-200 flex items-center gap-1"
                title={`iOS ${browserCapabilities.safariVersion} detected - Tap mode recommended`}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1.2 }}
              >
                <AlertTriangle className="w-3 h-3" />
                <span className="text-xs font-medium hidden sm:inline">iOS</span>
              </motion.div>
            )}
            
            {/* Input Mode Toggle */}
            <motion.button
              onClick={() => actions.setDragMode(!gameState.dragMode)}
              className={`flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all ${
                gameState.dragMode 
                  ? 'bg-gray-900 text-white border border-gray-900' 
                  : 'bg-gray-100 text-gray-700 border border-gray-200 hover:bg-gray-200'
              }`}
              title={gameState.dragMode ? 'Switch to click mode' : 'Switch to drag mode'}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 }}
            >
              <motion.div
                animate={{ rotate: gameState.dragMode ? 0 : 15 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                {gameState.dragMode ? (
                  <Move className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                ) : (
                  <MousePointer className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                )}
              </motion.div>
              <span className="hidden sm:inline">{gameState.dragMode ? 'Drag' : 'Click'}</span>
              <span className="sm:hidden">{gameState.dragMode ? 'Dr' : 'Cl'}</span>
            </motion.button>
            
            {/* Icon buttons */}
            {isAdmin && (
              <motion.button
                onClick={onShowAdminModal}
                className="p-2 sm:p-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600 hover:text-gray-900 transition-all border border-gray-200"
                title="Admin: Manage Puzzles"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.75 }}
              >
                <Database className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.button>
            )}
            
            {isAdmin && (
              <motion.button
                onClick={() => actions.clearGameState()}
                className="p-2 sm:p-2.5 bg-red-50 hover:bg-red-100 rounded-lg text-red-600 hover:text-red-700 transition-all border border-red-200"
                title="Clear saved progress (testing)"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 }}
              >
                <Trash2 className="w-4 h-4 sm:w-5 sm:h-5" />
              </motion.button>
            )}
            
            <motion.button
              onClick={() => actions.shuffleWords()}
              className="p-2 sm:p-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600 hover:text-gray-900 transition-all border border-gray-200"
              title="Shuffle words"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95, rotate: 180 }}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9 }}
            >
              <Shuffle className="w-4 h-4 sm:w-5 sm:h-5" />
            </motion.button>

            <motion.button
              onClick={onShowHelp}
              className="p-2 sm:p-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600 hover:text-gray-900 transition-all border border-gray-200"
              title="Help & Tutorial"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.95 }}
            >
              <HelpCircle className="w-4 h-4 sm:w-5 sm:h-5" />
            </motion.button>

            <motion.button
              onClick={onShowSettings}
              className="p-2 sm:p-2.5 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-600 hover:text-gray-900 transition-all border border-gray-200"
              title="Settings"
              whileHover={{ scale: 1.05, rotate: 90 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.0 }}
            >
              <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
            </motion.button>
          </div>
      </div>
    </motion.div>
  );
};

export default GameHeader;